import React, { useState } from 'react';

import { Box, DialogContent } from '@mui/material';
import { IconHelpRoundFilled, IconInfoOutlined } from '@maiia/design-system';
import { css, styled } from '@mui/material/styles';
import Dialog from '@/components/molecules/Dialog/Dialog';

type IconSize = 'small' | 'medium' | 'large';

const styledIconInfoSize: Record<IconSize, string> = {
  small: '1.2em',
  medium: '1.4em',
  large: '1.6em',
};

const styledIconInfoTranslate: Record<IconSize, string> = {
  small: 'translateY(-10%)',
  medium: 'translateY(-5%)',
  large: 'translateY(-5%)',
};

type IconProps = {
  iconSize: IconSize;
  iconColor?: string;
  onClick: (event: Event) => void;
};

const StyledIconInfo = styled(IconInfoOutlined, {
  shouldForwardProp: propName =>
    propName !== 'iconSize' && propName !== 'iconColor',
})<IconProps>(
  ({ theme, iconSize, iconColor }) => css`
    cursor: pointer;
    font-size: ${theme.typography.body1.fontSize};
    color: ${iconColor || theme.palette.primary.dark};
    height: ${styledIconInfoSize[iconSize]};
    width: auto;
    transform: ${styledIconInfoTranslate[
      iconSize
    ]}; // Align vertically perfectly
  `,
);

export const StyledIconHelp = styled(IconHelpRoundFilled, {
  shouldForwardProp: propName =>
    propName !== 'iconSize' && propName !== 'iconColor',
})<IconProps>(
  ({ theme, iconSize, iconColor }) => css`
    cursor: pointer;
    font-size: ${theme.typography.body1.fontSize};
    color: ${iconColor || theme.palette.primary.dark};
    height: ${styledIconInfoSize[iconSize]};
    width: auto;
    transform: ${styledIconInfoTranslate[
      iconSize
    ]}; // Align vertically perfectly
  `,
);

type Props = {
  title: string;
  message: React.ReactNode | string;
  Icon?: React.FC<IconProps>;
  iconSize?: IconSize;
  iconColor?: string;

  // Optional props for specific cases like for analytics events
  onOpen?: () => void;
  onClose?: () => void;
};

const IconModalIndication = (props: Props) => {
  const {
    title,
    message,
    Icon = StyledIconInfo,
    iconSize = 'medium',
    iconColor,
    onOpen,
    onClose,
    ...rest
  } = props;
  const [isOpen, setIsOpen] = useState(false);

  const onDialogOpen = (event: Event) => {
    // To disable the propagation of the event in the case where the icon is
    // in a clickable element (ex : in the insurance accordion, on the account page)
    event.stopPropagation();

    if (onOpen) onOpen();

    setIsOpen(true);
  };

  const onDialogClose = event => {
    event.stopPropagation();

    if (onClose) onClose();

    setIsOpen(false);
  };

  return (
    <Box component="span" ml={1}>
      <Icon
        iconSize={iconSize}
        iconColor={iconColor}
        onClick={event => onDialogOpen(event)}
        {...rest}
      />
      <Dialog
        open={isOpen}
        onClose={onDialogClose}
        title={title}
        onBackdropClick={onDialogClose}
        // Without that, if we click anywhere on the modal, the accordions will
        // open and close.
        onClick={event => event.stopPropagation()}
      >
        <DialogContent
          sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-wrap' }}
          data-cy="dialog-content"
        >
          {message}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IconModalIndication;
